"use client";
import * as React from "react";
import { LoaderOne } from "./loader";
import { cn } from "@/lib/utils";

export interface PageLoaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Optional message to display below the loader
   */
  message?: string;
  /**
   * Whether to use full screen height (min-h-screen) or container height (h-full)
   * @default "screen"
   */
  variant?: "screen" | "container" | "section";
}

/**
 * Unified page-level loading component for consistent loading states across the app.
 * Use this component for all page-level and section-level loading states.
 *
 * @example
 * // Full screen loading
 * if (isLoading) return <PageLoader message="Chargement..." />;
 *
 * @example
 * // Section loading
 * if (isLoading) return <PageLoader variant="section" />;
 */
const PageLoader = React.forwardRef<HTMLDivElement, PageLoaderProps>(
  ({ className, message, variant = "screen", ...props }, ref) => {
    const variantStyles = {
      screen: "min-h-screen",
      container: "h-full min-h-[400px]",
      section: "py-12",
    };

    return (
      <div
        ref={ref}
        role="status"
        aria-label={message || "Chargement..."}
        className={cn(
          "flex flex-col items-center justify-center bg-black",
          variantStyles[variant],
          className
        )}
        {...props}
      >
        <LoaderOne />
        {message && (
          <p className="text-white/60 mt-4 text-sm">{message}</p>
        )}
        <span className="sr-only">{message || "Chargement..."}</span>
      </div>
    );
  }
);

PageLoader.displayName = "PageLoader";

export { PageLoader };
